import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight, DownloadSimple, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const peso = (n) =>
  "₱" + Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BUCKETS = [
  ["regular", "Regular"],
  ["ot", "Overtime"],
  ["rest_day", "Rest day"],
  ["holiday", "Holiday"],
  ["night_diff", "Night differential"],
  ["leave", "Paid leave"],
];

/**
 * The guard's own payslip.
 *
 * ⚠️ This screen is the missing half of a feature that was otherwise finished.
 * Payslips were computed, snapshotted with the rate tables they used, totalled
 * and rendered to a PDF -- and `READ_ROLES` let payroll, admins and auditors
 * read them while returning 403 to the guard whose hours produced every figure
 * on the page. A guard asking what they had been paid had to ring the office.
 *
 * It shows the WHOLE arithmetic rather than the net: hours by premium bucket,
 * the three statutory deductions against the guard's own membership numbers,
 * withholding, and anything else taken off. Someone who can only see the bottom
 * line cannot tell a short payment from a normal one, and raising it is the only
 * remedy they have.
 */
export default function MobilePay() {
  const [slips, setSlips] = useState(null);
  const [open, setOpen] = useState(null);
  const [ben, setBen] = useState(null);

  useEffect(() => {
    api.get("/payroll/my-payslips")
      .then((r) => setSlips(r.data))
      .catch((e) => { setSlips([]); toast.error(errMsg(e)); });
    // ⚠️ A failure here must not take the payslips down with it. These are two
    // independent answers to two different questions, and a guard who cannot
    // reach one is still entitled to the other.
    api.get("/my/benefits").then((r) => setBen(r.data)).catch(() => setBen(false));
  }, []);

  const download = async (p) => {
    try {
      const res = await api.get(
        `/payroll/periods/${p.period_id}/payslips/${p.slip.id}.pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-${p.period_name.replace(/[^\w]+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="p-4" data-testid="mobile-pay">
      {ben && <Accruals b={ben} />}

      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-3">My payslips</div>

      {slips === null && <div className="font-mono text-xs text-muted-foreground border border-border p-6">Loading…</div>}

      {slips !== null && slips.length === 0 && (
        <div className="border border-border p-6 text-center" data-testid="no-payslips">
          <div className="font-mono text-xs text-muted-foreground">No payslip has been issued to you yet.</div>
          <div className="text-[11px] text-muted-foreground mt-2 leading-snug">
            One appears here when the office closes a cut-off you worked. Your hours are
            already recorded — you can check them on the Shifts tab.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(slips || []).map((p) => {
          const s = p.slip;
          const isOpen = open === p.run_id;
          return (
            <div key={p.run_id} className="border border-border bg-card" data-testid={`payslip-${p.run_ref}`}>
              <button
                type="button"
                className="w-full text-left p-4"
                data-testid={`payslip-toggle-${p.run_ref}`}
                onClick={() => setOpen(isOpen ? null : p.run_id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-muted-foreground">{p.run_ref}</span>
                  {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                </div>
                <div className="font-heading font-bold text-base leading-tight mt-0.5">{p.period_name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{p.start_date} → {p.end_date}</div>
                <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-border">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Take-home</span>
                  <span className="font-heading font-black text-2xl tabular-nums" data-testid={`net-${p.run_ref}`}>{peso(s.net)}</span>
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-4" data-testid={`payslip-detail-${p.run_ref}`}>
                  {p.unreviewed_tables?.length > 0 && (
                    <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-2.5 flex gap-2">
                      <Warning size={15} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                      <div className="text-[11px] leading-snug">
                        <span className="font-bold">Provisional.</span> The {p.unreviewed_tables.join(", ")} rate
                        {p.unreviewed_tables.length > 1 ? "s have" : " has"} not been confirmed against the current
                        circular, so a deduction below may still be corrected.
                      </div>
                    </div>
                  )}

                  <Section title="What you earned">
                    {BUCKETS.map(([k, label]) => {
                      const b = s.earnings?.[k];
                      if (!b || !b.hours) return null;
                      return <Row key={k} label={`${label} · ${b.hours}h @ ${peso(b.rate)}`} value={peso(b.amount)} />;
                    })}
                    {(s.additions || []).map((a, i) => (
                      <Row key={`a${i}`} label={a.label || a.type || "Addition"} value={peso(a.amount)} />
                    ))}
                    <Row label="Gross pay" value={peso(s.gross)} strong />
                  </Section>

                  <Section title="What was deducted">
                    <Row label="SSS" value={"-" + peso(s.statutory?.sss?.employee_this_period)} sub={s.sss_no} />
                    <Row label="PhilHealth" value={"-" + peso(s.statutory?.philhealth?.employee_this_period)} sub={s.philhealth_no} />
                    <Row label="Pag-IBIG" value={"-" + peso(s.statutory?.pagibig?.employee_this_period)} sub={s.pagibig_no} />
                    <Row label="Withholding tax" value={"-" + peso(s.withholding?.this_period)} sub={s.tin} />
                    {(s.other_deductions || []).map((d, i) => (
                      <Row key={`d${i}`} label={d.label || d.type || "Deduction"} value={"-" + peso(d.amount)} />
                    ))}
                    <Row label="Total deductions" value={"-" + peso(s.deductions_total)} strong />
                  </Section>

                  <div className="flex items-baseline justify-between border-t-2 border-foreground pt-2">
                    <span className="font-heading font-bold text-sm">NET PAY</span>
                    <span className="font-heading font-black text-xl tabular-nums">{peso(s.net)}</span>
                  </div>

                  {/* The employer share. It is not money in this guard's hand this
                      fortnight and the wording says so -- but it is credited to
                      their own membership record, and most guards have never been
                      shown that it exists at all. */}
                  <div className="font-mono text-[10px] text-muted-foreground leading-relaxed border-t border-border pt-2">
                    The agency also paid {peso(s.statutory?.employer_total_this_period)} into your SSS,
                    PhilHealth and Pag-IBIG for this cut-off. That is on top of the figures above and
                    goes to your membership record, not to this payslip.
                  </div>

                  <Button
                    variant="outline" className="w-full rounded-none h-11"
                    data-testid={`payslip-pdf-${p.run_ref}`} onClick={() => download(p)}
                  >
                    <DownloadSimple size={16} className="mr-2" /> Download PDF
                  </Button>
                  <div className="text-[10px] text-muted-foreground text-center leading-snug">
                    A figure wrong? Raise it before the next cut-off closes — your supervisor's
                    number is on the Profile tab.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * What the agency owes this guard beyond the last payslip.
 *
 * ⚠️ IT EXISTS BECAUSE THE BOND SCREEN MADE THINGS WORSE BEFORE IT MADE THEM
 * BETTER. A cash bond deduction now appears on every payslip, so a guard can
 * see P150 taken from them each cut-off and be told it is refundable -- while
 * the running balance lived on an office screen they cannot open. Being shown
 * a deduction with no balance is the grievance the ledger was built to stop.
 *
 * The 13th month is EARNED SO FAR, never projected. A forecast of what somebody
 * will be paid in December, on their own phone, is read as a promise.
 */
function Accruals({ b }) {
  const t = b.thirteenth_month;
  const l = b.service_incentive_leave;
  const c = b.cash_bond;
  return (
    <div className="mb-5" data-testid="my-benefits">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-3">
        What you have built up
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Tile label={`13th month, ${b.year}`} value={peso(t.amount)}
              sub={t.periods_paid ? `from ${peso(t.basic_earned)} basic over ${t.periods_paid} cut-off(s)`
                                  : "no cut-off paid yet this year"}
              testid="ben-13th" />
        <Tile label="Leave you can cash in" value={peso(l.cash_value)}
              sub={l.entitled_days
                ? `${l.balance_days} of ${l.entitled_days} day(s) left at ${peso(l.daily_rate)}/day`
                : `vests at ${l.vests_after_days} days of service — you are at ${l.tenure_days ?? "?"}`}
              testid="ben-sil" />
      </div>

      <div className="border border-border bg-card p-3 mt-2" data-testid="ben-bond">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            Cash bond held for you
          </span>
          <span className="font-heading font-black text-xl tabular-nums">{peso(c.balance)}</span>
        </div>
        {c.deducted > 0 ? (
          <>
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
              {peso(c.deducted)} taken so far · {peso(c.refunded)} returned
            </div>
            <div className="text-[11px] leading-snug text-muted-foreground mt-2">
              {c.basis} If a cut-off takes more than you expect, raise it before the next one
              closes — every deduction is itemised on the payslip below.
            </div>
          </>
        ) : (
          <div className="text-[11px] leading-snug text-muted-foreground mt-1">
            Nothing has been deducted from you as a bond.
          </div>
        )}
      </div>

      <div className="text-[10px] leading-snug text-muted-foreground mt-2">
        {t.note} These figures come from the same payroll runs the office reads, so what you
        see here is what they see.
      </div>
    </div>
  );
}

function Tile({ label, value, sub, testid }) {
  return (
    <div className="border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground leading-tight">
        {label}
      </div>
      <div className="font-heading font-black text-xl tabular-nums mt-0.5">{value}</div>
      <div className="text-[9px] text-muted-foreground leading-snug mt-0.5">{sub}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-1.5">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, sub, strong }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? "border-t border-border pt-1.5 mt-1.5" : ""}`}>
      <span className={`text-xs leading-snug ${strong ? "font-bold" : "text-muted-foreground"}`}>
        {label}
        {sub && <span className="block font-mono text-[9px] opacity-70">{sub}</span>}
      </span>
      <span className={`font-mono text-xs tabular-nums shrink-0 ${strong ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}
